import { ECONOMY_LABELS, LITRES_PER } from "./constants";
import { freshState } from "./defaults";
import type { Car, Entry, GarageState } from "./types";

/** Reads a number the way the current app does (`num`): "", null, undefined and non-numbers become null. */
export function num(v: unknown): number | null {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);

/**
 * Cleans saved data exactly like the current app (`normalize`): fills missing settings and
 * meta from `base`, drops cars without id or plate and entries whose car is gone, and falls
 * back to L / km/L / km for unknown units. Unknown fields are kept.
 *
 * Throws when the saved value isn't an object, so the caller treats it as corrupt (keeps a
 * copy aside and starts fresh), like the current app does for `null`.
 */
export function normalize(input: unknown, base: GarageState = freshState()): GarageState {
  if (!isObj(input)) throw new TypeError("Saved garage data is not an object");
  const st = input;
  const settings = isObj(st.settings) ? st.settings : {};
  const out: GarageState = {
    ...base,
    ...st,
    settings: { ...base.settings, ...settings } as GarageState["settings"],
    meta: { ...base.meta, ...(isObj(st.meta) ? st.meta : {}) },
    cars: [],
    log: [],
  };
  if (!Array.isArray(out.settings.items)) out.settings.items = base.settings.items;
  if (!Array.isArray(out.settings.others)) out.settings.others = base.settings.others;
  out.cars = (Array.isArray(st.cars) ? st.cars : []).filter((c): c is Car => isObj(c) && !!c.id && !!c.plate);
  const ids = new Set(out.cars.map((c) => c.id));
  out.log = (Array.isArray(st.log) ? st.log : []).filter((e): e is Entry => isObj(e) && !!e.id && ids.has(e.car as string));
  if (!Object.hasOwn(LITRES_PER, out.settings.vol)) out.settings.vol = "L";
  if (!Object.hasOwn(ECONOMY_LABELS, out.settings.econ)) out.settings.econ = "km/L";
  if (out.settings.dist !== "mi") out.settings.dist = "km";
  return out;
}
